import React, { useState } from 'react';
import { Bookmark, ChevronDown, Plus, Trash2, X, Check } from 'lucide-react';

/**
 * SavedViewBar — A reusable drop-in component that renders saved-view status,
 * a dropdown picker, save dialog, and clear controls.
 * 
 * Props:
 *  - savedViews: Array of view objects
 *  - activeViewId: Currently applied view ID (or null)
 *  - viewsLoading: boolean
 *  - onApplyView(view): callback
 *  - onSaveView(name): callback (async)
 *  - onDeleteView(viewId): callback (async)
 *  - onClearFilters(): callback
 *  - hasActiveFilters: boolean, whether any non-default filters are set
 */
export default function SavedViewBar({
    savedViews = [],
    activeViewId,
    viewsLoading,
    onApplyView,
    onSaveView,
    onDeleteView,
    onClearFilters,
    hasActiveFilters
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [newName, setNewName] = useState('');
    const [savingInProgress, setSavingInProgress] = useState(false);

    const activeView = savedViews.find(v => v._id === activeViewId);

    const handleSave = async () => {
        if (!newName.trim()) return;
        setSavingInProgress(true);
        try {
            await onSaveView(newName.trim());
            setNewName('');
            setIsSaving(false);
        } catch (_) { /* handled upstream */ }
        finally { setSavingInProgress(false); }
    };

    return (
        <div className="flex items-center gap-2 text-sm">
            {/* View Selector Dropdown */}
            <div className="relative">
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-border rounded-lg bg-card hover:bg-muted/50 text-foreground font-medium transition-colors shadow-sm"
                >
                    <Bookmark className="w-3.5 h-3.5 text-indigo-500" />
                    {activeView ? activeView.name : 'Views'}
                    <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground/50 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen && (
                    <div className="absolute left-0 mt-1 w-64 bg-popover border border-border rounded-xl shadow-lg z-50 overflow-hidden">
                        <div className="p-2 border-b border-border bg-muted/20">
                            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Saved Views</span>
                        </div>
                        <div className="max-h-48 overflow-y-auto divide-y">
                            {savedViews.length === 0 ? (
                                <div className="p-3 text-xs text-gray-400 text-center">No saved views yet.</div>
                            ) : savedViews.map(view => (
                                <div
                                    key={view._id}
                                    className={`flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-indigo-50 transition-colors ${activeViewId === view._id ? 'bg-indigo-50 border-l-2 border-indigo-500' : ''}`}
                                >
                                    <span
                                        className="text-sm font-medium text-foreground flex-1 truncate"
                                        onClick={() => { onApplyView(view); setIsOpen(false); }}
                                    >
                                        {activeViewId === view._id && <Check className="w-3 h-3 inline mr-1 text-indigo-600" />}
                                        {view.name}
                                    </span>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onDeleteView(view._id); }}
                                        className="p-1 text-muted-foreground hover:text-destructive transition-colors rounded"
                                        title="Delete view"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="p-2 border-t border-border bg-muted/20">
                            {!isSaving ? (
                                <button
                                    onClick={() => setIsSaving(true)}
                                    className="flex items-center gap-1 text-xs text-indigo-600 font-semibold hover:text-indigo-800 transition-colors w-full justify-center py-1"
                                    disabled={!hasActiveFilters}
                                >
                                    <Plus className="w-3 h-3" /> Save Current View
                                </button>
                            ) : (
                                <div className="flex items-center gap-1">
                                    <input
                                        autoFocus
                                        value={newName}
                                        onChange={e => setNewName(e.target.value)}
                                        placeholder="View name..."
                                        className="flex-1 text-xs border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                                        onKeyDown={e => e.key === 'Enter' && handleSave()}
                                    />
                                    <button onClick={handleSave} disabled={savingInProgress || !newName.trim()} className="p-1 text-indigo-600 hover:text-indigo-800 disabled:opacity-50">
                                        <Check className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => { setIsSaving(false); setNewName(''); }} className="p-1 text-muted-foreground hover:text-foreground">
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Active Filter Indicator / Clear */}
            {hasActiveFilters && (
                <button
                    onClick={onClearFilters}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive font-medium transition-colors bg-muted/30 hover:bg-destructive/10 px-2 py-1 rounded-md border border-border"
                >
                    <X className="w-3 h-3" /> Clear Filters
                </button>
            )}
        </div>
    );
}
